import type { Box, Fit } from './framing'

export type PhotoChangeKind = 'featured' | 'replace_figure' | 'insert_figure' | 'remove_figure' | 'caption' | 'framing' | 'trim'
export type PhotoRequestStatus = 'queued' | 'running' | 'ready' | 'failed' | 'applied' | 'discarded'
export type PhotoSide = { asset_id: number | null; src: string | null; width: number | null; height: number | null }

/** One proposed change. Coordinates are fractions of the image now in that position. */
export type PhotoChange = {
  id: string
  kind: PhotoChangeKind
  target: 'featured' | 'figure'
  n: number | null
  figure_asset_id: number | null
  asset_id: number | null
  after_paragraph: number | null
  after_text: string | null
  caption: string | null
  text: string | null
  focal: [number, number] | null
  subject_box: Box | null
  fit: Fit | null
  box: Box | null
  before: PhotoSide | null
  after: PhotoSide | null
  outcome: null | 'applied' | 'discarded' | 'refused'
  outcome_note: string | null
}

export type PhotoRequest = {
  id: number
  status: PhotoRequestStatus
  instruction: string
  reply: string | null
  changes: PhotoChange[]
  error: string | null
  created_by: string
  created_at: string
  finished_at: string | null
  cost_cents: number | null
}

export const isWorking = (r: PhotoRequest): boolean => r.status === 'queued' || r.status === 'running'

/** The changes still waiting for the editor. */
export const pendingChanges = (r: PhotoRequest): PhotoChange[] => r.changes.filter((c) => c.outcome == null)

/** A ready request with nothing left to apply (the helper proposed nothing): the editor can only dismiss it. */
export const canDismiss = (r: PhotoRequest): boolean => r.status === 'ready' && pendingChanges(r).length === 0

/** The caption a swapped or added photo will carry; with none, the photo publishes without one. */
export function captionLine(c: PhotoChange): string | null {
  if (c.kind !== 'replace_figure' && c.kind !== 'insert_figure') return c.caption || null
  return c.caption || 'No caption'
}

export function changeTitle(c: PhotoChange): string {
  const lead = c.target === 'featured'
  switch (c.kind) {
    case 'featured': return c.asset_id == null ? 'Remove the lead photo' : 'New lead photo'
    case 'replace_figure': return `Swap photo ${c.n}`
    case 'insert_figure': return `Add a photo after paragraph ${c.after_paragraph}`
    case 'remove_figure': return `Remove photo ${c.n}`
    case 'caption': return lead ? 'New caption for the lead photo' : `New caption for photo ${c.n}`
    case 'framing': return lead ? 'Reframe the lead photo' : `Reframe photo ${c.n}`
    case 'trim': return lead ? 'Trim the lead photo' : `Trim photo ${c.n}`
  }
}

export function outcomeText(c: PhotoChange): string {
  if (c.outcome === 'applied') return 'Applied'
  if (c.outcome === 'discarded') return 'Discarded'
  return `Not applied: ${c.outcome_note ?? ''}`.trimEnd()
}
